import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { useRef, useState } from 'react'

import { EyeCloseIcon, EyeOpenIcon, LockIcon } from '../../../icons'
import { InputProps } from '../input'
import BaseInput from './base-input'
import { getBaseInputProps } from './input-props'
import InputWrapper from './input-wrapper'

interface PasswordInputProps extends Omit<
  InputProps,
  'type' | 'icon' | 'iconStyle' | 'iconToTheRight'
> {}

const PasswordInput: React.FC<PasswordInputProps> = ({
  label,
  active,
  errorMessage,
  wrapperClassName,
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false)
  const ref = useRef<HTMLInputElement>(null)
  const baseInputProps = getBaseInputProps(props)

  const isActive = active || !!props.value
  const Eye = showPassword ? EyeOpenIcon : EyeCloseIcon

  const toggleShowPassword = () => setShowPassword((prev) => !prev)

  return (
    <InputWrapper
      label={label}
      active={isActive}
      icon={LockIcon}
      errorMessage={errorMessage}
      wrapperClassName={wrapperClassName}
      {...props}
    >
      {(_, setIsActive) => (
        <div
          className={cn(
            'flex items-center justify-between py-2 gap-1 cursor-text',
            props.disabled && 'opacity-50 cursor-not-allowed'
          )}
          onClick={() => {
            const inputElement = ref.current

            // If input is not focused, focus it
            if (inputElement && document.activeElement !== inputElement) {
              inputElement.focus()
              const length = inputElement.value.length
              setTimeout(() => {
                inputElement.setSelectionRange(length, length)
              }, 0)
            }
          }}
        >
          <BaseInput
            {...baseInputProps}
            ref={ref}
            className="w-full"
            type={showPassword ? 'text' : 'password'}
            setIsActive={setIsActive}
            aria-label={label}
            aria-invalid={!!errorMessage}
            aria-details="password-input"
          />

          <button type="button" className="flex" onClick={toggleShowPassword}>
            <Eye color={tailwindColors.grey.light.active} height="20px" width="20px" />
          </button>
        </div>
      )}
    </InputWrapper>
  )
}

export default PasswordInput
